import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { getEnv } from 'mobx-state-tree';
import { reaction } from 'mobx';
import { Observer } from 'mobx-react';
import { useTheme } from '@mui/material/styles';
import { useBreakpointDown } from '@shared/hooks/useBreakpoint';
import Typography from '@mui/material/Typography';
import membersIcon from '@assets/members-icon.svg'

import { Avatar } from '@shared/components/Avatar';
import { Table } from '@shared/components/Table';
import { NoResultsView } from '@shared/components/NoResultsView';
import EditIcon from '@assets/edit.svg';
import NeoIcon from '@assets/competencies-icon.svg';

import {
    useUsersGridUIStore,
    useUserUIStore,
    useUserPermissionsUIStore,
    useUserProfileUIStore,
    useGroupsUIStore
} from '@core/useStores';
import { IStoresEnv } from '@core/storesEnv';
import { useStyles } from './UsersList.styles'
import { NoUsersInGroup } from 'Users/components/NoUsersInGroup';
import { UserRole } from 'Users/domain/UserRole';

import { SearchForm } from '@shared/components/SearchForm';
import { messages } from 'Users/users.messages';
import {
    IPageQueryStoreSnapshotIn,
    IUserStore,
} from 'Users/stores';
import { ThemeProps } from '@styles/theme';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { Checkbox } from '@mui/material';
import { UserModal } from 'Users/components/UserModal';
import { RoutePaths } from '@core/routes/RoutePaths';
import { useNavigate } from 'react-router-dom';
import { USERS_IN_GROUP_ID } from '@shared/constants';

type GroupMembersCtxType = {
    memberIds: string[];
    onToggle: (user: IUserStore, add: boolean) => void;
    onSelectAll: () => void;
    onDeselectAll: () => void;
};

const GroupMembersCtx = React.createContext<GroupMembersCtxType | null>(null);

const GROUP_USERS_LIMIT = 1000;

type FetchDataType = {
    pageIndex: number;
    sortBy: {
        id: string;
        desc: boolean;
    }[];
};

const NoInfo = () => {
    const styles = useGridStyles();

    return <div className={styles.noInfo}>{messages['usersPage.noInfo']}</div>;
};

const columnHelper = createColumnHelper<IUserStore>();

const columnsData = [
    columnHelper.display({
        id: 'include',
        meta: { cardRole: 'selection' },
        header: () => {
            const ctx = React.useContext(GroupMembersCtx);
            const usersGridUIStore = useUsersGridUIStore();
            const groupsUIStore = useGroupsUIStore();
            if (!ctx || !groupsUIStore.massEdit) {return <></>;}

            return (
                <Observer>
                    {() => {
                        const total = usersGridUIStore.users.length;
                        const selectedCount = ctx.memberIds.length;
                        const allSelected = selectedCount === total && total > 0;
                        const someSelected = selectedCount > 0 && !allSelected;

                        return (
                            <Checkbox
                                indeterminate={someSelected}
                                checked={allSelected}
                                onClick={() => {
                                    if (allSelected) {ctx.onDeselectAll();} else {ctx.onSelectAll();}
                                }}
                            />
                        );
                    }}
                </Observer>
            );
        },
        minSize: 50,
        maxSize: 50,
        enableSorting: false,
        cell: ({ cell: {row}}) => {
            const styles = useGridStyles();
            const user = row.original;
            const ctx = React.useContext(GroupMembersCtx);
            const checked = ctx?.memberIds.includes(user.id) ?? false;

            return (
                <div className={styles.userWrapper}>
                    <Checkbox
                        checked={checked}
                        onClick={() => ctx?.onToggle(user, !checked)}
                    />
                </div>
            );
        },
    }),
    columnHelper.accessor('fullName', {
        header: messages['usersPage.table.fullName.header'],
        meta: { cardRole: 'title' },
        size: 60,
        cell: ({ cell: { row }, getValue }) => {
            const styles = useGridStyles();
            const { firstName, lastName, avatar, email } = row.original as {
                firstName: string;
                lastName: string;
                avatar: string;
                email: string;
            };

            return (
                <div className={styles.userWrapper}>
                    <Avatar
                        firstName={firstName}
                        lastName={lastName}
                        image={avatar}
                        className={styles.userAvatar}
                    />
                    <div className={styles.userNameWrapper}>
                        <span className={styles.ellipsis}>{getValue()}</span>
                        {email && <span className={styles.userEmail}>{email}</span>}
                    </div>
                </div>
            );
        },
    }),
    columnHelper.accessor('roles', {
        header: messages['usersPage.table.roles.header'],
        meta: { cardRole: 'chip' },
        size: 40,
        cell: ({ cell }) => {
            const styles = useGridStyles();
            const value = cell.getValue();

            return value.length ? (
                <span className={styles.capitalized}>
                    {value[0].name.toLowerCase()}
                </span>
            ) : (
                <NoInfo />
            );
        },
    }),
    columnHelper.display({
        id: 'edit',
        meta: { cardRole: 'actions' },
        header: () => {
            return <></>;
        },
        minSize: 40,
        maxSize: 110,
        enableSorting: false,
        cell: ({ cell }) => {
            const styles = useGridStyles();
            const row = cell.row;
            const userUIStore = useUserUIStore();
            const userId = useUserProfileUIStore().userInfo.id;
            const userPermissionsUIStore = useUserPermissionsUIStore();
            const navigate = useNavigate();

            const original: Record<string, any> = row.original;
            const userRoles = original.roles.map((role: UserRole) => role.name);

            const canEdit =
                original.id === userId ||
                userPermissionsUIStore.canEditUser(userRoles);

            const onCellClick = () => {
                userUIStore.setEditUserId(original.id);
                userUIStore.toggleUserModalOpen(true);
            };

            const onUserClick = () => {
                navigate(`/${RoutePaths.groups}/analytics/${original.id}`)
            };

            return (
                <div className={styles.actionsWrapper}>
                    <button
                        onClick={onUserClick}
                        className={styles.button}
                        title={messages['usersPage.table.goToAnalytics']}
                    >
                        <img src={NeoIcon} style={{
                            width: 24,
                            height: 24,
                        }}/>
                    </button>
                    <button
                        onClick={() => canEdit && onCellClick()}
                        disabled={!canEdit}
                        className={styles.button}
                        title={messages['usersPage.table.editUser']}
                    >
                        <img src={EditIcon} />
                    </button>
                </div>
            );
        },
    }),
];

const useGridStyles = () => {
    const theme = useTheme<ThemeProps>();
    const styles = useStyles({ theme });

    return styles;
};

export const UsersList: React.FC = () => {
    const theme = useTheme<ThemeProps>();
    const styles = useStyles({ theme });
    const isTabletLayout = useBreakpointDown('desktop');

    const usersGridUIStore = useUsersGridUIStore();
    const userUIStore = useUserUIStore();
    const groupsUIStore = useGroupsUIStore();
    const { filtersOptions, groups } = getEnv<IStoresEnv>(usersGridUIStore);

    const [memberIds, setMemberIds] = useState<string[]>(() => {
        if (groupsUIStore.massEdit && groups.selectedGroup) {
            return usersGridUIStore.users
                .filter(u => u.groups.some(g => g.id === groups.selectedGroup?.id))
                .map(u => u.id);
        }

        return [];
    });

    const memberIdsRef = useRef(memberIds);
    memberIdsRef.current = memberIds;

    useEffect(() => {
        const disposer = reaction(
            () => groupsUIStore.massEdit,
            massEdit => {
                if (massEdit) {
                    const ids = usersGridUIStore.users
                        .filter(u => u.groups.some(g => g.id === groups.selectedGroup?.id))
                        .map(u => u.id);
                    setMemberIds(ids);
                } else {
                    setMemberIds([]);
                }
            },
        );

        return () => disposer();
    }, []);

    const onToggle = useCallback(async (user: IUserStore, add: boolean) => {
        setMemberIds(prev => add ? [...prev, user.id] : prev.filter(id => id !== user.id));
        let groupIds = user.groups.map(g => g.id);
        if (add) {
            if (groups.selectedGroup?.id) {groupIds.push(groups.selectedGroup.id);}
        } else {
            groupIds = groupIds.filter(id => id !== groups.selectedGroup?.id);
        }
        await userUIStore.editUser({
            id: user.id,
            firstName: user.firstName,
            lastName: user.lastName,
            department: user.department,
            email: user.email,
            roleId: user.roles.map(r => r.id)[0],
            groupIds,
            procedureIds: user.procedures.map(p => p.id),
        });
    }, [groups, userUIStore]);

    const onSelectAll = useCallback(async () => {
        const notInGroup = usersGridUIStore.users.filter(u => !memberIdsRef.current.includes(u.id));
        setMemberIds(usersGridUIStore.users.map(u => u.id));
        await Promise.all(
            notInGroup.map(user => {
                const groupIds = [...user.groups.map(g => g.id)];
                if (groups.selectedGroup?.id) {groupIds.push(groups.selectedGroup.id);}

                return userUIStore.editUser({
                    id: user.id,
                    firstName: user.firstName,
                    lastName: user.lastName,
                    department: user.department,
                    email: user.email,
                    roleId: user.roles.map(r => r.id)[0],
                    groupIds,
                    procedureIds: user.procedures.map(p => p.id),
                });
            })
        );
    }, [usersGridUIStore, groups, userUIStore]);

    const onDeselectAll = useCallback(async () => {
        const inGroup = usersGridUIStore.users.filter(u => memberIdsRef.current.includes(u.id));
        setMemberIds([]);
        await Promise.all(
            inGroup.map(user => {
                const groupIds = user.groups.map(g => g.id).filter(id => id !== groups.selectedGroup?.id);

                return userUIStore.editUser({
                    id: user.id,
                    firstName: user.firstName,
                    lastName: user.lastName,
                    department: user.department,
                    email: user.email,
                    roleId: user.roles.map(r => r.id)[0],
                    groupIds,
                    procedureIds: user.procedures.map(p => p.id),
                });
            })
        );
    }, [usersGridUIStore, groups, userUIStore]);

    const membersCtxValue = useMemo(() => ({
        memberIds,
        onToggle,
        onSelectAll,
        onDeselectAll,
    }), [memberIds, onToggle, onSelectAll, onDeselectAll]);

    const onSearchTextChanged = (searchText: string) => {
        if (searchText && searchText.length === 1) {
            return;
        }

        usersGridUIStore.setParams({
            page: 0,
            searchText,
        });
    };

    const initialSortState = {
        sorting: [
            {
                id: 'fullName',
                desc: false,
            },
        ],
    };

    const columns: ColumnDef<IUserStore, any>[] = React.useMemo(() => {
        if (!isTabletLayout) {
            return columnsData;
        }

        return columnsData.filter((column: any) => !['roles'].includes(column.id ?? column.accessorKey));
    }, [isTabletLayout]);

    useEffect(() => {
        usersGridUIStore.cleanUp();

        usersGridUIStore.setParams({
            page: 0,
            limit: GROUP_USERS_LIMIT,
            inGroup: USERS_IN_GROUP_ID,
            inGroupId: groups.selectedGroup!.id,
        });

        usersGridUIStore.togglePageActivity(true);
        usersGridUIStore.load();
        usersGridUIStore.loadStatistics();
        filtersOptions.loadFilterOptions();

        return () => {
            usersGridUIStore.togglePageActivity(false);
            usersGridUIStore.cleanUp();
        };
    }, []);

    const onFetchData = ({ pageIndex, sortBy }: FetchDataType) => {
        const params: IPageQueryStoreSnapshotIn = { page: pageIndex };

        if (sortBy && sortBy.length) {
            params.sortFieldName = sortBy[0].id;
            params.sortDirection = Number(sortBy[0].desc);
        }
        if (usersGridUIStore.isActivePage) {
            usersGridUIStore.setParams(params);
        }
    };

    const onInviteUserModalClose = () => {
        const params = usersGridUIStore.inviteUser.getInviteUserFields;

        usersGridUIStore.inviteUser.setInviteUserFields({
            ...params,
            errors: null,
        });
        userUIStore.clearEditUserId();
        userUIStore.toggleUserModalOpen(false);
    };

    return (
        <GroupMembersCtx.Provider value={membersCtxValue}>
            <Observer>
                {() => {
                    const data = usersGridUIStore.users.toJSON();
                    const preparedData = data.map(row => ({
                        ...row,
                    }));

                    let genericColumns = columns as ColumnDef<{}, any>[];

                    if(!groupsUIStore.massEdit){
                        const checkboxColumn = genericColumns[0];
                        genericColumns = genericColumns.filter(c => c != checkboxColumn) as ColumnDef<{}, any>[];
                    }

                    const selectedGroupMemebers = groupsUIStore.massEdit
                        ? memberIds.length
                        : usersGridUIStore.pagination.totalEntities;

                    return (
                        <>
                            {
                                userUIStore.userModalOpened &&
                            <UserModal
                                isOpen={userUIStore.userModalOpened}
                                onRequestClose={onInviteUserModalClose}
                            />
                            }
                            <div className={styles.mainWrapper}>
                                <div className={styles.headerWrapper}>
                                    <div className={styles.titleWrapper}>
                                        <Typography variant="h6" className={styles.sectionTitle}>
                                        Users
                                        </Typography>
                                        <div className={styles.memebersCounter}>
                                            <img src={membersIcon} />
                                            <span>
                                                {selectedGroupMemebers} members
                                            </span>
                                        </div>
                                    </div>
                                    <SearchForm
                                        initialValues={{
                                            search: usersGridUIStore.requestParams
                                                .getStorageParams
                                                ? usersGridUIStore.requestParams
                                                    .getStorageParams.searchText
                                                : '',
                                        }}
                                        className={styles.searchPanel}
                                        onSearchTextChanged={onSearchTextChanged}
                                    />
                                </div>
                                {groups.selectedGroup && (
                                    <div className={styles.tableWrapper}>
                                        {usersGridUIStore.isTableVisible && (
                                            <Table
                                                data={preparedData}
                                                columns={genericColumns}
                                                fetchData={onFetchData}
                                                isLoading={
                                                    usersGridUIStore.status.isLoading
                                                }
                                                useSkeleton={true}
                                                paginationStatus={{
                                                    ...usersGridUIStore.pagination,
                                                }}
                                                paginationDisabled
                                                initialState={initialSortState}
                                            />
                                        )}
                                    </div>
                                )
                                }

                                {usersGridUIStore.noFilteringResults && (
                                    <NoResultsView entityName="users" />
                                )}

                                {usersGridUIStore.noItemsInGroup && (
                                    <NoUsersInGroup />
                                )}
                            </div>
                        </>
                    );
                }}
            </Observer>
        </GroupMembersCtx.Provider>
    );
};
